import styled, { css } from 'styled-components/macro'
import { transparentize } from 'polished'
import { BtnBurger, Button } from 'components/ui'

export const Wrap = styled.header`
  grid-area: Header;
  position: sticky;
  top: 0;
  margin: 0;
  width: 100%;
  height: 5.4rem;
  background-color: ${(props) => props.theme.color.elementBg};
  border-bottom: 0.1rem solid ${(props) => props.theme.color.elementBorder};
  z-index: ${(props) => props.theme.zIndex.navigation};
  ${(props) => props.theme.breakpoints.greaterThan('lg')`
    height: 5.6rem;
  `};
`

export const Container = styled.div`
  margin: 0 auto;
  padding: 1.2rem 1.2rem 1.2rem 0;
  height: 5.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    justify-content: flex-start;
  `};
`

export const Nav = styled.nav<{ isOpen: boolean }>`
  padding: 0 1.6rem;
  position: absolute;
  left: 0;
  top: 5.4rem;
  right: 0;
  padding: 0;
  height: calc(100vh - 5.4rem);
  display: flex;
  flex-direction: column;
  transition: all 0.3 ease-in;
  overflow: auto;
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.isOpen ? props.theme.opacity.default : props.theme.opacity.invisible)};
  ${(props) => props.theme.breakpoints.greaterThan('sm')`
    position: absolute;
    left: auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${props.theme.color.elementBg};
    overflow: unset;
  `};
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    position: static;
    top: auto;
    left: auto;
    right: auto;
    margin: 0;
    padding: 0;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: auto;
    white-space: nowrap;
    background-color: ${props.theme.color.transparent};
    visibility: visible;
    opacity: ${props.theme.opacity.default};
  `};
`

export const List = styled.ul`
  margin: 0;
  padding: 0 0 3rem;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  height: auto;
  list-style: none;
  gap: 0;
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    flex: 0 0 auto;
    height: 100%;
    list-style: none;
  `};
`

export const BadgeChain = styled.span`
  position: absolute;
  top: 0.4rem;
  right: -0.6rem;
  padding: 0.1rem 0.4rem;
  display: inline-block;
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 0.9rem;
  line-height: 1.44;
  letter-spacing: -0.03rem;
  text-transform: capitalize;
  color: ${(props) => props.theme.color.text};
  background-color: ${(props) => props.theme.color.default};
  border-radius: 0.6rem;
`

export const ListItem = styled.li<{ badge?: string }>`
  position: relative;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  font-family: ${(props) => props.theme.font.family.default};
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 1.4rem;
  line-height: 1.43;
  color: ${(props) => props.theme.color.textSecondary};
  &:not(:last-child) {
    border-bottom: 0.1rem solid ${(props) => transparentize(0.93, props.theme.color.text)};
  }
  &::before {
    display: none;
  }

  a {
    position: relative;
    padding: 1.8rem;
    width: 100%;
    text-align: center;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 0.3rem;
      transition: all 0.26s ease;
    }
    &:hover {
      &::after {
        background-color: ${(props) => props.theme.color.elementBgThird};
      }
    }
    &.activeNavLink {
      &::after {
        background-color: ${(props) => props.theme.color.buttonBgThird};
      }
    }
  }

  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    font-size: 1.2rem;
    line-height: 1.33;
    border-left: 0.1rem solid ${props.theme.color.elementBorder};
    &:first-child {
      border-left: 0.1rem solid ${props.theme.color.elementBorder};
    }
    &:last-child {
      border-right: 0.1rem solid ${props.theme.color.elementBorder};
    }
    &:not(:last-child) {
      border-bottom: none;
    }
  `};

  ${(props) => props.theme.breakpoints.greaterThan('lg')`
    font-size: 1.4rem;
    line-height: 1.43;
  `};

  ${(props) => props.theme.breakpoints.greaterThan('xl')`
    a {
      padding: 1.8rem 3rem;
    }
  `};
`

export const UserWrap = styled.div<{ desktop?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
  ${(props) =>
    props.desktop &&
    css`
      width: auto;
      display: none;
      ${(props) => props.theme.breakpoints.greaterThan('sm')`
        display: flex;
      `};
    `}
`

export const ButtonWrap = styled.div`
  padding: 0;
  display: flex;
  align-items: center;
  gap: 1.2rem;
`

export const ContentWrap = styled.div<{ type?: string; isConnected?: boolean }>`
  padding: 0;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  ${(props) =>
    props.type === 'mobile' &&
    css`
      position: fixed;
      bottom: 0;
      left: 0;
      margin: 0;
      padding: 1rem 1.6rem;
      width: 100%;
      display: inline-grid;
      grid-template-columns: repeat(2, auto);
      grid-column-gap: 1rem;
      justify-content: space-between;
      align-items: center;
      background-color: ${(props) => props.theme.color.elementBg};
      border-top: 0.1rem solid ${(props) => props.theme.color.elementBorder};
      ${(props) =>
        props.isConnected &&
        css`
          grid-template-columns: repeat(3, auto);
        `}
      ${(props) => props.theme.breakpoints.greaterThan('sm')`
        position: static;
        margin: 0 0 0 auto;
        padding: 0;
        width: auto;
        grid-column-gap: 1.6rem;
        background-color: ${props.theme.color.transparent};
        border-top: none;
      `};
      ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
        display: none;
      `};
    `}
  ${(props) =>
    props.type === 'desktop' &&
    css`
      display: none;
      ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
        display: flex;
      `};
    `}
`

export const HistoryButton = styled(Button)`
  width: 4.4rem;
  height: 4.4rem;
  color: ${(props) => props.theme.color.textPrimary};
  background-color: ${(props) => props.theme.color.buttonBg};
  &:disabled {
    opacity: ${(props) => props.theme.opacity.opacity02};
  }
  &:hover:not([disabled]),
  &:focus:not([disabled]),
  &:active:not([disabled]) {
    background-color: ${(props) => props.theme.color.buttonBg};
    box-shadow: 0 0 0 0.3rem ${(props) => transparentize(0.5, props.theme.color.buttonBg)};
  }
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    width: inherit;
    height: inherit;
  `};
`

export const HistoryText = styled.span`
  display: none;
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    display: inline;
  `};
`

export const HistoryIcon = styled.span`
  display: inline;
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    display: none;
  `};
`

export const Connection = styled.div`
  display: flex;
`

export const ConnectionMobile = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  margin: 0;
  padding: 1.2rem 1.6rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  background-color: ${(props) => props.theme.color.elementBg};
  border-top: 0.1rem solid ${(props) => transparentize(0.95, props.theme.color.black)};
  ${(props) => props.theme.breakpoints.greaterThan('sm')`
    position: static;
    margin: 0 0 0 auto;
    padding: 1.2rem 0;
    width: auto;
    display: flex;
    align-items: center;
    background-color: ${props.theme.color.transparent};
    border-top: none;
  `};
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    display: none;
  `};
`

export const ConnectBtn = styled(Button)`
  width: 100%;
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    width: auto;
  `};
`

export const Burger = styled(BtnBurger)<{ onClick: VoidFunction }>`
  margin: 0 0 0 1.2rem;
  color: ${(props) => props.theme.color.textSecondary};
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    display: none;
  `};
`

export const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 5.4rem;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => props.theme.color.elementBg};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.isOpen ? props.theme.opacity.default : props.theme.opacity.invisible)};
  transition: opacity 0.2s ease-out;
  z-index: ${(props) => props.theme.zIndex.negative};
  ${(props) => props.theme.breakpoints.greaterThan('mdMax')`
    display: none;
    visibility: hidden;
  `};
`

export const WrapperChain = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`

export const ChainIconWrap = styled.div`
  width: 100%;
  flex: 0 0 auto;
  ${(props) => props.theme.breakpoints.greaterThan('xsm')`
    width: auto;
  `};
`

export const ChainValue = styled.span`
  display: none;
  font-size: 1.4rem;
  ${(props) => props.theme.breakpoints.greaterThan('xsm')`
    display: inline;
  `};
`

export const ChainSelectWrap = styled.div`
  margin: 0 auto 0 0;
  width: 8.8rem;
  ${(props) => props.theme.breakpoints.greaterThan('xsm')`
    width: 16.8rem;
  `};
  ${(props) => props.theme.breakpoints.greaterThan('sm')`
    margin: 0;
  `}
`
